import { DATASETS } from '../components/Constants';

interface VDAData {
  variantStrID: string;
  geneSymbol: string;
  geneDescription: string;
  geneNcbiIDs: number[];
  numGenesAssociatedToDisease: number;
  numDiseasesAssociatedToVariant?: number;
  numDiseasesAssociatedToVariant_ALL?: number;
  numVariantsAssociatedToDisease: number;
  numPMIDsupportingAssociation: number;
  alleles_ref: string;
  alleles_alt: string[];
  diseaseUMLSCUI: string;
  diseaseName: string;
  pmid: string;
  score: number;
  EI: number | null;
  numberPmidsWithChemsIncludedInEvidence: number;
  numberChemsIncludedInEvidence: number;
  numberPmidsWithChemsFiltered: number;
  coord: string;
}

class VDAModel {
  variantStrID: string;
  geneSymbol: string;
  geneDescription: string;
  geneNcbiIDs: number[];
  numGenesAssociatedToDisease: number;
  numDiseasesAssociatedToVariant: number;
  numVariantsAssociatedToDisease: number;
  numPMIDsupportingAssociation: number;
  alleles_ref: string;
  alleles_alt: string[];
  diseaseUMLSCUI: string;
  diseaseName: string;
  pmid: string;
  score: number;
  EI: number | null;
  numPmidsWithChemsEvidence: number;
  numberChemsIncludedInEvidence: number;
  numPmidsWithChemsFiltered: number;
  coord: string;

  constructor(data: VDAData) {
    this.variantStrID = data.variantStrID;
    this.geneSymbol = data.geneSymbol;
    this.geneDescription = data.geneDescription;
    this.geneNcbiIDs = data.geneNcbiIDs;
    this.numGenesAssociatedToDisease = data.numGenesAssociatedToDisease;
    this.numDiseasesAssociatedToVariant = data.numDiseasesAssociatedToVariant
      ? data.numDiseasesAssociatedToVariant
      : data.numDiseasesAssociatedToVariant_ALL || 0;
    this.numVariantsAssociatedToDisease = data.numVariantsAssociatedToDisease;
    this.numPMIDsupportingAssociation = data.numPMIDsupportingAssociation;
    this.alleles_ref = data.alleles_ref;
    this.alleles_alt = data.alleles_alt;
    this.diseaseUMLSCUI = data.diseaseUMLSCUI;
    this.diseaseName = data.diseaseName;
    this.pmid = data.pmid;
    this.score = data.score;
    this.EI = this.transformEIValue(data.EI);
    this.numPmidsWithChemsEvidence = data.numberPmidsWithChemsIncludedInEvidence;
    this.numberChemsIncludedInEvidence = data.numberChemsIncludedInEvidence;
    this.numPmidsWithChemsFiltered = data.numberPmidsWithChemsFiltered;
    this.coord = data.coord;
  }

  transformEIValue(eiValue: number | null): number | null {
    return eiValue !== null && eiValue !== undefined ? Number(eiValue.toFixed(2)) : null;
  }
}

class VDATable extends VDAModel {
  constructor(data: VDAData) {
    super(data);
  }

  get valueDisease(): string {
    return `UMLS_${this.diseaseUMLSCUI}`;
  }

  get valueVariant(): string[] {
    return [this.variantStrID];
  }

  get valueGene(): number[] {
    return this.geneNcbiIDs;
  }

  get alleles(): string {
    return this.alleles_ref + '/' + this.alleles_alt.join(',');
  }

  getName(): Array<{ name: string; value: string; title: string }> {
    const name = [
      {
        name: this.diseaseName + ', ' + this.diseaseUMLSCUI,
        value: this.valueDisease,
        title: DATASETS.DISEASES,
      },
    ];

    return name;
  }

  getNameDV(): Array<{ name: string; value: string; title: string }> {
    const nameDV = [
      {
        name: `${this.variantStrID}${this.geneSymbol ? `, ${this.geneSymbol}` : ''}`,
        value: this.variantStrID,
        title: DATASETS.VARIANTS,
      },
      {
        name: this.diseaseName + ', ' + this.diseaseUMLSCUI,
        value: this.valueDisease,
        title: DATASETS.DISEASES,
      },
    ];

    return nameDV;
  }

  getNameDVG(identifier: string): Array<{ name: string; value: string | number[]; title: string }> {
    const nameDV: Array<{ name: string; value: string | number[]; title: string }> = [
      {
        name: `${this.variantStrID}${this.geneSymbol ? `, ${this.geneSymbol}` : ''}`,
        value: this.variantStrID,
        title: DATASETS.VARIANTS,
      },
      {
        name: this.diseaseName + ', ' + this.diseaseUMLSCUI,
        value: this.valueDisease,
        title: DATASETS.DISEASES,
      },
    ];

    if (identifier === DATASETS.GENES) {
      nameDV.push({
        name: this.geneSymbol + ', ' + this.geneDescription,
        value: this.geneNcbiIDs,
        title: DATASETS.GENES,
      });
    }

    return nameDV;
  }

  getNameVG(identifier: string): Array<{ name: string; value: string | number | number[]; title: string }> {
    const nameVG: Array<{ name: string; value: string | number | number[]; title: string }> = [
      {
        name: `${this.variantStrID}${this.geneSymbol ? `, ${this.geneSymbol}` : ''}`,
        value: this.variantStrID,
        title: DATASETS.VARIANTS,
      },
    ];

    if (identifier === DATASETS.GENES) {
      nameVG.push({
        name: this.geneSymbol + ', ' + this.geneDescription,
        value: this.geneNcbiIDs.length > 1 ? this.geneNcbiIDs : this.geneNcbiIDs[0],
        title: DATASETS.GENES,
      });
    }

    return nameVG;
  }
}

export { VDAModel, VDATable };
